import React from 'react';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAssessmentCtx } from './AssessmentContext';
import { MarketingConsentCheckbox } from '../MarketingConsentCheckbox';

export default function LeadCaptureStep() {
  const { handleLeadInfoChange, handleSubmit, leadInfo, questionsData, results, setCurrentQuestionIndex, setCurrentStep, submitting } = useAssessmentCtx();
  const isFormValid = leadInfo.first_name && leadInfo.last_name && leadInfo.email && 
                     leadInfo.company_name && leadInfo.job_title && leadInfo.team_size && leadInfo.industry;

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-violet-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 py-6 px-4">
        <div className="max-w-xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => { setCurrentStep('questions'); setCurrentQuestionIndex(questionsData.questions.length - 1); }}
            className="text-slate-400 hover:text-white text-sm flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Questions
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-xl mx-auto px-4 py-8 sm:py-12">
        {/* Success Indicator */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-4 border border-violet-500/30">
            <CheckCircle className="h-8 w-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Assessment Complete!
          </h1>
          <p className="text-slate-400">
            Enter your details to see your personalized results
          </p>
        </div>

        {/* Lead Capture Form */}
        <form onSubmit={handleSubmit} className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800 p-6 sm:p-8">
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm mb-2 block">First Name *</label>
                <Input
                  name="first_name"
                  value={leadInfo.first_name}
                  onChange={handleLeadInfoChange}
                  placeholder="John"
                  required
                  data-testid="lead-first-name"
                  className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm mb-2 block">Last Name *</label>
                <Input
                  name="last_name"
                  value={leadInfo.last_name}
                  onChange={handleLeadInfoChange}
                  placeholder="Smith"
                  required
                  data-testid="lead-last-name"
                  className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                />
              </div>
            </div>

            <div>
              <label className="text-slate-400 text-sm mb-2 block">Email *</label>
              <Input
                type="email"
                name="email"
                value={leadInfo.email}
                onChange={handleLeadInfoChange}
                placeholder="john@company.com"
                required
                data-testid="lead-email"
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="text-slate-400 text-sm mb-2 block">Company Name *</label>
              <Input
                name="company_name"
                value={leadInfo.company_name}
                onChange={handleLeadInfoChange}
                placeholder="Acme Corporation"
                required
                data-testid="lead-company"
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="text-slate-400 text-sm mb-2 block">Job Title *</label>
              <Input
                name="job_title"
                value={leadInfo.job_title}
                onChange={handleLeadInfoChange}
                placeholder="VP of Operations"
                required
                data-testid="lead-job-title"
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm mb-2 block">Team Size *</label>
                <select
                  name="team_size"
                  value={leadInfo.team_size}
                  onChange={handleLeadInfoChange}
                  required
                  data-testid="lead-team-size"
                  className="w-full h-10 px-3 rounded-md bg-slate-950 border border-slate-700 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                >
                  <option value="">Select team size...</option>
                  {questionsData.team_size_options.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-slate-400 text-sm mb-2 block">Industry *</label>
                <select
                  name="industry"
                  value={leadInfo.industry}
                  onChange={handleLeadInfoChange}
                  required
                  data-testid="lead-industry"
                  className="w-full h-10 px-3 rounded-md bg-slate-950 border border-slate-700 text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                >
                  <option value="">Select industry...</option>
                  {questionsData.industry_options.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <MarketingConsentCheckbox
              checked={leadInfo.marketing_consent}
              onChange={(v) => setLeadInfo((prev) => ({ ...prev, marketing_consent: v }))}
              note="Your results are provided either way."
              testId="assessment-marketing-consent"
            />
          </div>

          <Button
            type="submit"
            disabled={!isFormValid || submitting}
            data-testid="view-results-btn"
            className="w-full mt-6 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold py-6 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Calculating Your Score...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                View My Results
                <ArrowRight className="h-5 w-5" />
              </span>
            )}
          </Button>

          <p className="text-slate-500 text-xs text-center mt-4">
            Your information is secure and will only be used to provide your assessment results and relevant insights.
          </p>
        </form>
      </main>
    </div>
  );
}
